import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';

const AppointmentConfirmation = () => {
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      setVerifying(true);

      // Get reference from URL query params
      const params = new URLSearchParams(location.search);
      const reference = params.get('reference');

      if (!reference) {
        setError('Invalid payment reference. Please contact support.');
        setVerifying(false);
        setLoading(false);
        return;
      }

      // Get stored appointment details from localStorage
      let appointmentId = localStorage.getItem('pendingAppointmentId');
      const storedReference = localStorage.getItem('pendingAppointmentReference');

      // Fallback: Extract appointment ID from reference if localStorage is empty
      // Reference format: APPT-{appointmentId}-{timestamp}
      if (!appointmentId && reference.startsWith('APPT-')) {
        const parts = reference.split('-');
        // UUID has 5 parts, so appointment ID is parts 1-5 (indices 1-5)
        // APPT-c4abd009-ae71-4e86-9fed-b7e43a219e9d-1765023722488
        if (parts.length >= 7) {
          appointmentId = parts.slice(1, 6).join('-');
          console.log('[AppointmentConfirmation] Extracted appointmentId from reference:', appointmentId);
        }
      }

      if (!appointmentId) {
        setError('Could not determine appointment. Please contact support.');
        setVerifying(false);
        setLoading(false);
        return;
      }

      // Only verify reference match if we have a stored reference (skip if using fallback)
      if (storedReference && reference !== storedReference) {
        console.warn('[AppointmentConfirmation] Reference mismatch, but proceeding with verification');
        // Don't block - proceed with verification anyway since the backend will validate
      }

      // Verify payment with backend
      const response = await axios.post('/appointments/verify-payment', {
        reference,
        appointmentId
      });

      if (response.data.success) {
        setSuccess(true);
        setAppointment(response.data.appointment);

        // Clear stored data
        localStorage.removeItem('pendingAppointmentId');
        localStorage.removeItem('pendingAppointmentReference');
      } else {
        setError(response.data.error || 'Payment verification failed');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setError(err.response?.data?.error || 'Failed to verify payment. Please contact support.');
    } finally {
      setVerifying(false);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ fontSize: '18px', color: '#6b7280' }}>
            {verifying ? 'Verifying payment...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb',
        padding: '24px'
      }}>
        <div style={{
          maxWidth: '480px',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#fee2e2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <svg width="32" height="32" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>
            Payment Failed
          </h2>
          <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '24px' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (success && appointment) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb',
        padding: '24px'
      }}>
        <div style={{
          maxWidth: '600px',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#d1fae5',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <svg width="32" height="32" fill="none" stroke="#10b981" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '12px',
            textAlign: 'center'
          }}>
            {appointment.status === 'PENDING' ? 'Appointment Request Received!' : 'Appointment Confirmed!'}
          </h2>

          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            {appointment.status === 'PENDING'
              ? 'Your payment has been received. The merchant will review and confirm your appointment shortly.'
              : 'Your appointment has been confirmed. You will receive a confirmation email with all the details.'}
          </p>

          <div style={{
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '16px'
            }}>
              Appointment Details
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Service:</span>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                {appointment.serviceName}
              </p>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Date:</span>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                {formatDate(appointment.appointmentDate)}
              </p>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Time:</span>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                {appointment.appointmentTime}
              </p>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Duration:</span>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                {appointment.serviceDuration} minutes
              </p>
            </div>

            <div>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Customer:</span>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                {appointment.customerName}
              </p>
              <p style={{ fontSize: '14px', color: '#6b7280' }}>
                {appointment.customerEmail}
              </p>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center'
          }}>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AppointmentConfirmation;
